import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@tutlo/ui'
import { useState } from 'react'
import { Shell } from '../layout.tsx'
import { ApiError, useCapturePhone, useCreateBooking, useLead, useSlots } from '../lead.ts'
import { PhoneForm, type PhoneFormValue } from '../screens/booking/phone-form.tsx'
import { SlotSummary } from '../screens/booking/slot-summary.tsx'
import { SlotSummaryPanel } from '../screens/booking/slot-summary-panel.tsx'
import { TrustBadge } from '../screens/trust-badge.tsx'
import { validateSearch } from '../search.ts'
import { SLOT_DAYS } from './kalendarz.tsx'

export const Route = createFileRoute('/telefon')({
  validateSearch,
  component: PhoneRoute,
})

const FORM_ID = 'phone-form'

/**
 * Figma `227:6495`: wybrany termin + telefon + potwierdzenie (bez obietnicy holdu — umawiator go nie ma, D3).
 * „Potwierdź rezerwację” = `POST phone` → `POST bookings` (D3) → poczekalnia `/s/:token`. Slot z `?start=`.
 */
function PhoneRoute() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const lead = useLead()
  const slots = useSlots(SLOT_DAYS)
  const [bookingError, setBookingError] = useState<{ code: string; message: string } | null>(null)
  const capturePhone = useCapturePhone(lead.data?.id ?? '')
  const createBooking = useCreateBooking()

  if (lead.isPending) return <Shell nav={{}}>{null}</Shell>
  if (!lead.data) {
    navigate({ to: '/', replace: true })
    return null
  }
  const start = search.start
  if (!start || Number.isNaN(Date.parse(start))) {
    navigate({ to: '/kalendarz', replace: true })
    return null
  }
  const leadId = lead.data.id
  const durationMin = slots.data?.durationMin ?? 30
  const busy = capturePhone.isPending || createBooking.isPending

  const submit = async (value: PhoneFormValue) => {
    setBookingError(null)
    try {
      await capturePhone.mutateAsync({ ...value, consentPhone: true, consentAccount: true })
      await createBooking.mutateAsync({ leadId, start })
      navigate({ to: '/s/$token', params: { token: lead.data?.startToken ?? '' } })
    } catch (err) {
      if (
        err instanceof ApiError &&
        !['phone_invalid', 'bot_suspected', 'validation'].includes(err.code)
      ) {
        setBookingError({ code: err.code, message: err.message })
      }
    }
  }

  const phoneError =
    capturePhone.error instanceof ApiError
      ? capturePhone.error.code === 'phone_invalid'
        ? 'Sprawdź numer — powinien mieć 9 cyfr.'
        : capturePhone.error.code === 'validation'
          ? 'Sprawdź numer i zaznacz obie zgody.'
          : capturePhone.error.code === 'bot_suspected'
            ? 'Spróbuj jeszcze raz za chwilę.'
            : null
      : null

  return (
    <Shell
      nav={{ back: () => navigate({ to: '/kalendarz', search: { widok: 'dni' } }) }}
      ctaUnderContent
      aside={<SlotSummaryPanel slot={{ start, end: start }} durationMin={durationMin} />}
      cta={
        <div className="flex flex-col gap-sm">
          {bookingError ? <BookingErrorBox error={bookingError} /> : null}
          <Button type="submit" form={FORM_ID} loading={busy}>
            Potwierdź rezerwację
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-base pt-lg pb-base">
        <div className="desktop:hidden">
          <SlotSummary start={start} durationMin={durationMin} />
        </div>
        <PhoneForm id={FORM_ID} disabled={busy} apiError={phoneError} onSubmit={submit} />
        <div className="pt-md">
          <TrustBadge />
        </div>
      </div>
    </Shell>
  )
}

function BookingErrorBox({ error }: { error: { code: string; message: string } }) {
  const retry = error.code === 'slot_unavailable' || error.code === 'booking_invalid'
  if (error.code === 'existing_booking') {
    // Umawiator already holds a booking for this e-mail (made outside this funnel, e.g. Tutlo's
    // own qualifier): the mail from Tutlo has the details; the chat can reschedule.
    return (
      <p
        className="rounded-card-sm bg-warning-soft px-[14px] py-md text-label text-warning-text"
        role="alert"
      >
        Na ten adres jest już umówiona lekcja próbna. Szczegóły i link masz w mailu od Tutlo. Chcesz
        zmienić termin albo nie widzisz wiadomości?{' '}
        <Link to="/chat" className="font-bold underline">
          Napisz na czacie
        </Link>
      </p>
    )
  }
  return (
    <p
      className="rounded-card-sm bg-warning-soft px-[14px] py-md text-label text-warning-text"
      role="alert"
    >
      {error.message}
      {retry ? (
        <>
          {' '}
          <Link to="/kalendarz" search={{ widok: 'dni' }} className="font-bold underline">
            Wybierz inny termin
          </Link>
        </>
      ) : null}
    </p>
  )
}
